import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConfirmMessageModalContext } from '../confirm-message-modal/confirm-message-modal.component';
import { ModalHelper } from '@services/modal-helper.service';
import { AppState } from '@app/app.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { matchValidator } from '@widgets/validators/matching.validator';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { IUserProfile, UserProfileService } from '@ztarmobile/zwp-service-backend-v2';
import { passwordRulesValidator } from '@widgets/validators/password.validators';
import { ToastrHelperService } from '@services/toast-helper.service';

export type ChangePasswordModalData = {
  user: IUserProfile,
  customClass?: string,
}

@Component({
  selector: 'app-change-password-modal',
  templateUrl: './change-password-modal.component.html',
  styleUrl: './change-password-modal.component.scss'
})
export class ChangePasswordModalComponent {
  public step = 1;
  public currentPasswordForm: FormGroup;
  public showCurrentError = false;
  public showCurrentPassword = false;

  public newPasswordForm: FormGroup;
  public showNewPassword = false;
  public isNewPassFocused = false;
  public showConfirmPassword = false;
  public showMismatchError = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChangePasswordModalData,
    public dialog: MatDialogRef<ConfirmMessageModalContext>,
    private modalHelper: ModalHelper,
    private appState: AppState,
    private toastHelper: ToastrHelperService,
    private angularFireService: AngularFireAuth,
    private userProfileService: UserProfileService
  ) { }

  ngOnInit(): void {
    this.currentPasswordForm = new FormGroup({
      password: new FormControl('', [Validators.required])
    });

    this.newPasswordForm = new FormGroup({
      newPassword: new FormControl('', [Validators.required, passwordRulesValidator()]),
      confirmNewPassword: new FormControl('', [Validators.required])
    },
      { validators: matchValidator('newPassword', 'confirmNewPassword') }
    );
  }

  get newPassword() {
    return this.newPasswordForm.get('newPassword');
  }

  public submitCurrentPassword() {
    this.currentPasswordForm.markAllAsTouched();
    if (this.currentPasswordForm.invalid) {
      return;
    }
    this.appState.loading = true;
    const currentPassword = this.currentPasswordForm.controls.password.value;
    this.angularFireService.signInWithEmailAndPassword(this.data.user.email, currentPassword).then((userCred) => {
      this.appState.loading = false;
      this.step = 2;
    }, (error) => {
      this.appState.loading = false;
      if (error.code === 'auth/wrong-password') {
        this.showCurrentError = true
      } else {
        this.toastHelper.showAlert(error.message);
      }
    });
  }

  public updatePassword(): void {
    this.newPasswordForm.markAllAsTouched();
    if (this.newPasswordForm.invalid) {
      return;
    }

    this.appState.loading = true;
    const newPassword = this.newPasswordForm.controls.newPassword.value;

    this.userProfileService.changePassword({ email: this.data.user.email, password: newPassword }).then((result) => {
      this.angularFireService.signInWithEmailAndPassword(this.data.user.email, newPassword).then((auth) => {
        this.appState.loading = false;
        this.closeDialog(true);
        this.modalHelper.successModal(
          '',
          'Done',
          'secondary-success-modal no-massage',
          'Password Successfully Updated',
          '',
          'assets/icon/banner-success-icon.svg',
          true,
        );
      }, (error) => {
        this.showError();
        this.appState.loading = false;
      });
    }, (error) => {
      this.showError();
      this.appState.loading = false;
    });
  }

  private showError() {
    this.modalHelper.failureModal(
      'Your request cannot be processed at this moment. Please try again later.',
      'Got It',
      'secondary-fail-modal',
      'Something Went Wrong!',
      '',
      'assets/icon/circle-error.svg'
    );
    this.appState.loading = false;
  }

  preventInputFocus(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  beforeClose(): boolean {
    if (document.body.classList.contains('modal-open')) {
      document.body.classList.remove('modal-open');
    }
    return false;
  }

  closeDialog(res?): void {
    this.beforeClose();
    this.dialog.close(res);
  }

  cancel(): void {
    this.closeDialog(false);
  }
}
